/**
 * Hook that fetches menu config and transforms it into usable shapes
 * Wraps the single useGetMenuConfigQuery endpoint
 */

import { useMemo } from 'react'
import { menuWidgetApi } from '@/store/convenienceAccessor'
import { getEnvironmentConfig, getResolvedMenuId, isPreviewMode } from '@/config/environment'

export interface MenuSection {
  id: string
  title: string
  subtitle?: string
  description?: string
  order: number
  items: MenuItem[]
}

export interface MenuItem {
  id: string
  title: string
  short_description?: string
  product_image_url?: string
  hero_image_url?: string
  badge_labels?: string[]
  is_featured?: boolean
  order: number
  price: string
  base_price: string
  compare_at_price?: string
  currency: string
  product_type: 'meal' | 'combo' | 'ingredient'
  product_id: string
  product?: MealProduct | ComboProduct
}

export interface MealProduct {
  id: string
  title: string
  description?: string
  image_url?: string
  meal_type: 'builder' | 'fixed'
  base_price: string
  rows?: Row[]
  logic_rules?: LogicRule[]
}

export interface ComboProduct {
  id: string
  title: string
  description?: string
  image_url?: string
  base_price: string
  main_meal?: MealProduct
  groups?: ComboGroup[]
}

export interface Row {
  id: string
  title: string
  description?: string
  max_selections?: number
  order: number
  ingredients: RowIngredient[]
  variations?: RowVariation[]
}

export interface RowIngredient {
  id: string
  order: number
  is_default: boolean
  additional_price: string
  ingredient: Ingredient
}

export interface Ingredient {
  id: string
  name: string
  image_url?: string
  standalone_price?: string
  calories?: number
  protein?: number
  carbs?: number
  fat?: number
}

export interface RowVariation {
  id: string
  name: string
  key: string
  order: number
  is_default: boolean
  ingredients: RowIngredient[]
}

export interface ComboGroup {
  id: string
  name: string
  min_selections: number
  max_selections: number
  order: number
  options: ComboOption[]
}

export interface ComboOption {
  id: string
  is_default: boolean
  order: number
  additional_price: string
  ingredient: Ingredient
}

export interface LogicRule {
  id: string
  name: string
  operator: 'any' | 'all'
  priority: number
  is_active: boolean
  conditions: Condition[]
  effects: Effect[]
}

export interface Condition {
  id: string
  condition_type: string
  trigger_row_id?: string
  trigger_row_ingredient_id?: string
  metadata?: Record<string, unknown>
}

export interface Effect {
  id: string
  effect_type: string
  target_row_id?: string
  target_row_ingredient_id?: string
  metadata?: Record<string, unknown>
}

export interface MenuLocation {
  id: string
  location_name: string
  address?: string
  city?: string
  state?: string
  country?: string
  postal_code?: string
  latitude?: number
  longitude?: number
  phone?: string
  uber_eats_link?: string
  doordash_link?: string
}

export interface RestaurantHours {
  day_of_week: number
  open_time: string
  close_time: string
  is_closed: boolean
}

export interface RestaurantHoursException {
  date: string
  label: string
  open_time: string | null
  close_time: string | null
  is_closed: boolean
}

export interface RestaurantInfo {
  id: string
  title: string
  banner_image_url?: string
  logo_url?: string
  rating?: number
  review_count?: string
  cuisine_tags?: string[]
  colors?: {
    primary?: string
    secondary?: string
    background?: string
    text?: string
  }
  address?: string
  city?: string
  state?: string
  country?: string
  postal_code?: string
  phone?: string
  email?: string
  uber_eats_link?: string
  doordash_link?: string
  hours?: RestaurantHours[]
  hours_exceptions?: RestaurantHoursException[]
}

export interface MenuDataResult {
  // Restaurant info
  restaurant: RestaurantInfo | null

  // Menu structure
  menus: Array<{
    id: string
    title: string
    channel?: string
    sections: MenuSection[]
    locations?: MenuLocation[]
  }>

  // Flattened data for easy access
  allSections: MenuSection[]
  allItems: MenuItem[]
  categories: Array<{ id: string; name: string }>

  // Loading states
  isLoading: boolean
  isError: boolean
  error: unknown
}

/**
 * Main hook to fetch and transform menu data
 */
export function useMenuData(options?: {
  channel?: 'delivery' | 'pickup' | 'in_store' | 'other'
  skip?: boolean
}): MenuDataResult {
  const config = getEnvironmentConfig()
  const preview = isPreviewMode()
  const menuId = getResolvedMenuId()

  const { data, isLoading, isError, error } = menuWidgetApi.menuConfig.get(
    {
      restaurant_id: config.restaurantId,
      channel: options?.channel,
      ...(preview ? { preview: 'true' } : {}),
      ...(menuId ? { menu_id: menuId } : {}),
    },
    { skip: options?.skip }
  )

  // Transform the response into usable shapes
  const result = useMemo((): Omit<MenuDataResult, 'isLoading' | 'isError' | 'error'> => {
    if (!data) {
      return {
        restaurant: null,
        menus: [],
        allSections: [],
        allItems: [],
        categories: [],
      }
    }

    // Extract banner_image_url from first menu (if available)
    const bannerImageUrl = data.menus?.[0]?.banner_image_url
    // Logo URL might be at the root level or in the first menu
    const logoUrl = data.logo_url || data.menus?.[0]?.logo_url

    const info = data.restaurant_info
    const restaurant: RestaurantInfo = {
      id: data.id,
      title: data.title,
      banner_image_url: bannerImageUrl,
      logo_url: info?.logo_url || logoUrl,
      rating: data.rating,
      review_count: data.review_count,
      cuisine_tags: data.cuisine_tags,
      colors: data.colors,
      address: info?.address,
      city: info?.city,
      state: info?.state,
      country: info?.country,
      postal_code: info?.postal_code,
      phone: info?.phone,
      email: info?.email,
      uber_eats_link: info?.uber_eats_link,
      doordash_link: info?.doordash_link,
      hours: info?.hours,
      hours_exceptions: info?.hours_exceptions,
    }

    const menus = data.menus || []

    // Flatten all sections from all menus
    const allSections: MenuSection[] = menus.flatMap((menu: { sections?: MenuSection[] }) =>
      menu.sections || []
    )

    // Flatten all items from all sections
    const allItems: MenuItem[] = allSections.flatMap((section) =>
      section.items || []
    )

    // Get categories from products
    const categories = data.products?.categories || []

    return {
      restaurant,
      menus,
      allSections,
      allItems,
      categories,
    }
  }, [data])

  return {
    ...result,
    isLoading,
    isError,
    error,
  }
}

/**
 * Helper to calculate item total price with selected ingredients
 */
export function calculateItemPrice(
  basePrice: string | number,
  selectedIngredients: Array<{ additional_price?: string | number }>
): number {
  let total = typeof basePrice === 'string' ? parseFloat(basePrice) : basePrice

  for (const ingredient of selectedIngredients) {
    const additionalPrice = ingredient.additional_price
    if (additionalPrice) {
      total += typeof additionalPrice === 'string'
        ? parseFloat(additionalPrice)
        : additionalPrice
    }
  }

  return total
}

/**
 * Helper to calculate combo total price
 */
export function calculateComboPrice(
  basePrice: string | number,
  selectedOptions: Array<{ additional_price?: string | number }>
): number {
  return calculateItemPrice(basePrice, selectedOptions)
}
